"use client"

import React, { useMemo, useRef, useState, useTransition } from "react"
import { debounce } from "next/dist/server/utils"
import Link from "next/link"
import { fetchSuggestionsAction } from "@/actions/searchActions"
import {
  addProductToWishlistAction,
  getWishlistForUserAction,
  loadWishlistLineItemsAction,
  removeProductFromWishlistAction,
} from "@/actions/wishlistActions"
import { StoryblokComponent } from "@storyblok/react/rsc"
import { DownloadIcon, LoaderCircle, MinusIcon, PhoneIcon, PlusIcon, SearchIcon, TrashIcon } from "lucide-react"

import { SuggestionItem } from "@/lib/factfinder/factfinder-interfaces"
import { formatDate } from "@/lib/helper"
import { generateProductLink } from "@/lib/link-utils/link-utils"
import { AccountContext } from "@/lib/storefront/account/account-service"
import { WishListWithId } from "@/lib/storefront/account/wishlist-service"
import { OrderLineItemData } from "@/lib/storefront/order/order"
import { determinePrefixedProductId } from "@/lib/storefront/product/productHelper"
import { WishlistLineItemData } from "@/lib/storefront/wishlist/wishlist"
import { Button, Input } from "@/components/ui/components"
import { QuadProductImagesPreview } from "@/components/ui/components/quad-product-images-preview"
import { cn } from "@/components/ui/helpers/utils"
import AccountBackLinkClient from "@/components/account/AccountBackLinkClient"
import AccountOrderDetailsLineItemCardClient from "@/components/account/orders/detail/AccountOrderDetailsLineItemCardClient"
import ReorderAllButtonClient from "@/components/account/orders/detail/ReorderAllButtonClient"
import { DiscountActiveLine } from "@/components/account/profile/common/Discount"
import ImageWithFallback from "@/components/storyblok/common/ImageWithFallback"
import { UbAccountWishlistViewStoryblok } from "@/components/storyblok/component-types"

export default function AccountWishlistDetailClient(props: {
  blok: UbAccountWishlistViewStoryblok
  wishlist: WishListWithId
  wishlistItems: WishlistLineItemData[]
  accountContext: AccountContext
}) {
  const [wishlist, setWishlist] = useState(props.wishlist)
  const [wishlistItems, setWishlistItems] = useState(props.wishlistItems)
  const [deleteLoading, setDeleteLoading] = useState("")
  const [quantityLoading, setQuantityLoading] = useState("")
  const debouncedHandleChange = debounce(handleChange, 1000, 10000)

  const lineItems = useMemo(() => {
    return wishlistItems
      .map((wishline): OrderLineItemData => {
        const quantity = (wishline.wishlistLineItem?.attributes ?? []).find((a) => a.name == "desiredQuantity")
        const id = (wishline.wishlistLineItem?.attributes ?? []).find((a) => a.name == "id")

        return {
          product: wishline.product,
          orderLineItem: {
            id: id?.value ?? undefined,
            quantity: {
              value: quantity?.value?.value ?? wishline.product.minOrderQuantity ?? 1,
              unit: quantity?.value?.unit ?? wishline.product.baseUnit,
            },
            product: wishline.product.id,
            pricing: {
              singleBasePrice: wishline.prices?.data[0]?.prices?.SalePrice[0] ?? undefined,
              price: undefined, // TODO add price for quantity of products
            },
          },
        }
      })
      .sort((a, b) => (a.product?.id ?? "").localeCompare(b.product?.id ?? "", props.accountContext.isoLocale))
  }, [wishlistItems])
  const customerRebateActive = useMemo(
    () =>
      wishlistItems.some((li) =>
        li?.wishlistLineItem?.attributes?.some(
          (attr: any) =>
            attr.name === "UBPLIRebateRate" &&
            (attr.value?.includes("DISCOUNT_TYPE_CUSTOMER") || attr.value?.includes("DISCOUNT_TYPE_CATEGORY_CUSTOMER"))
        )
      ),
    [wishlistItems]
  )
  const imageUrls = useMemo(() => wishlistItems.map((li) => li.product.image), [wishlistItems])

  const [reloading, reloadingTransaction] = useTransition()

  async function triggerUpdate(cbFinal?: () => void) {
    reloadingTransaction(async () => {
      const newList = await getWishlistForUserAction(
        props.accountContext.customerData,
        props.accountContext.userData,
        props.wishlist.id
      )
      if (newList) {
        setWishlist(newList)
        const newItemList = await loadWishlistLineItemsAction(
          newList,
          props.accountContext.customerData?.customerNo ?? ""
        )
        setWishlistItems(newItemList)
      }
      if (cbFinal) {
        cbFinal()
      }
    })
  }

  function getLineItemByProductID(productId: string) {
    return lineItems.find((l) => l.product?.id === productId)
  }

  async function handleDelete(productId?: string) {
    if (!productId) {
      return
    }

    setDeleteLoading(productId)
    const lineItem = getLineItemByProductID(productId)
    if (lineItem) {
      await removeProductFromWishlistAction(props.wishlist.id, productId)
      await triggerUpdate(() => setDeleteLoading(""))
    } else {
      setDeleteLoading("")
    }
  }

  // We do this complicated, because the context switch between the Elements kills our debounce.

  function handleIncrease(productId: string | undefined, input: Element | null): string {
    if (!productId || !input) {
      return ""
    }
    const lineItem = getLineItemByProductID(productId)
    if (!lineItem) {
      return ""
    }
    const inputElement = input as HTMLInputElement
    if (!inputElement) {
      return ""
    }
    let quantityIn = parseInt(inputElement.value, 10)
    if (isNaN(quantityIn) || quantityIn < 1) {
      quantityIn = 0
    }

    const quantity = Math.max(
      quantityIn + (lineItem.product?.stepOrderQuantity ?? 1),
      lineItem.product?.minOrderQuantity ?? 1
    )
    inputElement.value = quantity.toString()
    return quantity.toString()
  }

  function handleDecrease(productId: string | undefined, input: Element | null): string {
    if (!productId || !input) {
      return ""
    }
    const lineItem = getLineItemByProductID(productId)
    if (!lineItem) {
      return ""
    }
    const inputElement = input as HTMLInputElement
    if (!inputElement) {
      return ""
    }
    let quantityIn = parseInt(inputElement.value, 10)
    if (isNaN(quantityIn) || quantityIn < 1) {
      quantityIn = 0
    }

    const quantity = Math.max(
      quantityIn - (lineItem.product?.stepOrderQuantity ?? 1),
      lineItem.product?.minOrderQuantity ?? 1
    )
    inputElement.value = quantity.toString()
    return quantity.toString()
  }

  async function handleChange(productId: string, inputQuantity: string) {
    let quantity = parseInt(inputQuantity, 10)
    if (productId == "" || isNaN(quantity) || quantity < 1) {
      return
    }
    const lineItem = getLineItemByProductID(productId)
    if (!lineItem) {
      return
    }
    setQuantityLoading(productId)

    if (
      lineItem.product?.stepOrderQuantity &&
      (lineItem.product?.stepOrderQuantity ?? 1) != 1 &&
      quantity % lineItem.product?.stepOrderQuantity != 0
    ) {
      quantity -= quantity % (lineItem.product?.stepOrderQuantity ?? 1)
    }
    quantity = Math.max(quantity, lineItem.product?.minOrderQuantity ?? 1)

    if (lineItem.orderLineItem.quantity.value < quantity) {
      await addProductToWishlistAction(
        props.wishlist.id,
        productId,
        quantity - lineItem.orderLineItem.quantity.value,
        false,
        true
      )
    } else {
      await removeProductFromWishlistAction(props.wishlist.id, productId)
      await addProductToWishlistAction(props.wishlist.id, productId, quantity, false, true)
    }

    await triggerUpdate(() => setQuantityLoading(""))
  }

  return (
    <div className={"text-sm"}>
      {/* Header */}
      <AccountBackLinkClient label={props.blok.label_common_back} />

      <div className={"flex items-center justify-between rounded-sm border border-gray-200 bg-white p-4"}>
        <div>
          <h1 className="flex flex-row flex-wrap gap-x-1 text-xl">
            <span className={"font-light"}>{(props.blok.label_wishlist ?? "Merkliste") + ": "}</span>
            <span className={"font-bold"}>{wishlist?.title}</span>
          </h1>
          {customerRebateActive && (
            <DiscountActiveLine
              discountLabel={props.blok.label_customer_rebate}
              activeText={props.blok.label_customer_rebate_active}
            />
          )}
          <p className={"flex flex-row flex-wrap gap-x-1"}>
            <span className={"font-light"}>
              {(props.blok.label_wishlist_creation_date ?? "Erstellungsdatum") + ": "}
            </span>
            <span>
              {formatDate(props.accountContext.isoLocale, new Date(wishlist?.creationDate ?? 0), {
                weekday: "long",
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </span>
          </p>
        </div>
        <QuadProductImagesPreview
          className={"size-32 gap-2 rounded-lg border p-1 *:rounded-lg"}
          productCount={wishlistItems.filter((i) => i.product != undefined).length}
          products={wishlistItems.map((i) => i.product).filter((i) => i != undefined)}
        />
      </div>
      <AccountWishlistDetailSearchClient
        blok={props.blok}
        sbLocale={props.accountContext.sbLocale}
        wishlistId={props.wishlist.id}
        onChange={triggerUpdate}
      />

      {/* Wishlist Items */}
      {lineItems.length == 0 ? (
        props.blok.empty_wishlist_message && (
          <div className="relative z-0 mt-6 rounded-sm border border-gray-200 bg-white p-4">
            {props.blok.empty_wishlist_message.map((blokInner) => (
              <StoryblokComponent key={blokInner._uid} blok={blokInner} />
            ))}
          </div>
        )
      ) : (
        <div className="mt-6 rounded-sm border border-gray-200 bg-white p-4">
          {reloading && <div className={"absolute inset-0 bg-gray-200/80 backdrop-blur"} />}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-xl font-semibold sm:flex-row sm:items-center">
            <h2>{props.blok.label_wishlist_details_overview}</h2>
            <div className="flex flex-row flex-wrap gap-2">
              <ReorderAllButtonClient
                items={(async () => lineItems)()}
                label_buy_again={props.blok.label_button_buy_again ?? ""}
                label_is_adding={props.blok.label_product_is_adding ?? ""}
              />

              <Link
                prefetch={false}
                target={"_blank"}
                href={(props.blok?.link_pdf_download ?? "/bestellung-pdf?WishlistID=") + props.wishlist.id}
              >
                <Button size={"smNoGrow"} variant={"outline"} className="bg-primary text-white">
                  <DownloadIcon className={"size-5"} />
                </Button>
              </Link>

              <Link prefetch={false} href={props.blok?.link_contact ?? "/info/de-de/kontaktformular"}>
                <Button size={"smNoGrow"} variant={"outline"} className="bg-primary text-white">
                  <PhoneIcon className={"size-5"} />
                </Button>
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-[max-content_auto_max-content_max-content] items-center gap-2 border-b pb-2 lg:grid-cols-[max-content_max-content_auto_max-content_max-content_max-content] lg:gap-4">
            <div
              className={
                "col-span-full grid w-full grid-cols-subgrid grid-rows-1 hyphens-auto text-xs font-semibold lg:text-sm"
              }
            >
              <div className={"max-lg:hidden"}></div>
              <div>{props.blok.label_artnr}</div>
              <div>{props.blok.label_article_column}</div>
              <div>{props.blok.label_quantity}</div>
              {/* Replace this line with the following ones, if we got sum prices again, also remove hideSumPrice flag */}
              <div>{props.blok.label_price_per_item}</div>
              {/*<div className={"hidden md:block"}>{props.blok.label_price_per_item}</div>*/}
              {/*<div className={"text-right"}>{props.blok.label_total_price_column}</div>*/}
            </div>
            {lineItems.map((item) => (
              <AccountOrderDetailsLineItemCardClient
                key={item.orderLineItem.id}
                item={item}
                isoLocale={props.accountContext.isoLocale}
                hideSumPrice={true}
                quantityElement={
                  <>
                    <div className={"flex flex-col gap-2 lg:flex-row"}>
                      <div
                        className={cn(
                          "flex w-12 flex-col items-center justify-center rounded-lg bg-gray-200 p-0 lg:w-auto lg:flex-row lg:px-2",
                          quantityLoading != "" ? "bg-gray-50" : ""
                        )}
                      >
                        {/* U SHALL NOT PUT ANYTHING BETWEEN BUTTONS AND INPUT */}
                        <button
                          className={cn(
                            "w-full rounded-t-lg py-0.5 max-lg:flex max-lg:justify-center lg:w-auto lg:rounded-full lg:py-1 lg:pl-4 lg:pr-2"
                          )}
                          onClick={(e) =>
                            debouncedHandleChange(
                              item.product?.id ?? "",
                              handleDecrease(item.product?.id, e.currentTarget.nextElementSibling)
                            )
                          }
                          disabled={quantityLoading != ""}
                          aria-label={props.blok.label_product_quantity_decrease}
                        >
                          <MinusIcon
                            className={cn("size-5", {
                              "text-muted-foreground opacity-20": quantityLoading != "",
                            })}
                          />
                        </button>
                        <input
                          type="text"
                          pattern="[0-9]*"
                          inputMode={"numeric"}
                          aria-label={props.blok.label_product_quantity}
                          disabled={quantityLoading != ""}
                          className={cn(
                            "h-9 w-12 border-transparent bg-transparent p-0 py-1 text-center text-base font-bold focus:rounded-md focus:border-2 focus:border-blue-400 focus:outline-none focus:ring-transparent lg:text-lg",
                            { "text-muted-foreground opacity-20": quantityLoading != "" }
                          )}
                          defaultValue={item.orderLineItem.quantity.value}
                          onChange={(e) => debouncedHandleChange(item.product?.id ?? "", e.target.value)}
                          onKeyDown={(event) => {
                            if (
                              !(
                                /[0-9]/.test(event.key) ||
                                event.code === "Backspace" ||
                                event.code === "Delete" ||
                                event.code === "Delete" ||
                                event.code === "ArrowLeft" ||
                                event.code === "ArrowRight"
                              )
                            ) {
                              event.preventDefault()
                            }
                          }}
                        />
                        <button
                          className={cn(
                            "w-full rounded-b-lg py-0.5 max-lg:flex max-lg:justify-center lg:w-auto lg:rounded-full lg:py-1  lg:pl-4 lg:pr-2"
                          )}
                          onClick={(e) =>
                            debouncedHandleChange(
                              item.product?.id ?? "",
                              handleIncrease(item.product?.id ?? "", e.currentTarget.previousElementSibling)
                            )
                          }
                          disabled={quantityLoading != ""}
                          aria-label={props.blok.label_product_quantity_increase}
                        >
                          <PlusIcon
                            className={cn("size-5", { "text-muted-foreground opacity-20": quantityLoading != "" })}
                          />
                        </button>
                        {/* U SHALL NOT PUT ANYTHING BETWEEN BUTTONS AND INPUT */}
                      </div>
                      <div className={"flex items-center justify-center"}>
                        <Button
                          onClick={() => handleDelete(item.product?.id)}
                          variant={"destructive"}
                          size={"iconNoGrow"}
                          disabled={deleteLoading == item.product?.id}
                        >
                          {deleteLoading == item.product?.id ? (
                            <LoaderCircle className={"size-5 motion-safe:animate-spin"} />
                          ) : (
                            <TrashIcon className={"size-5"} />
                          )}
                        </Button>
                      </div>
                    </div>
                  </>
                }
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

type AccountWishlistDetailSearchClientProps = {
  blok: UbAccountWishlistViewStoryblok
  sbLocale: string
  wishlistId: string
  onChange: () => Promise<void>
}

function AccountWishlistDetailSearchClient(props: AccountWishlistDetailSearchClientProps) {
  const debouncedHandleSearch = debounce(handleSearch, 500)
  const [loadingSearch, transitionSearch] = useTransition()
  const [loadingAddItem, transitionAddItem] = useTransition()
  const [currentAddItem, setCurrentAddItem] = useState("")
  const [foundProducts, setFoundProducts] = useState<SuggestionItem[] | null>(null)
  const searchFieldRef = useRef<HTMLInputElement>(null)

  async function handleSearch(input: string) {
    setFoundProducts(null)
    const query = input.trim()
    if (query.length < 3) {
      return
    }
    transitionSearch(async () => {
      const suggestions = await fetchSuggestionsAction(query, "products", props.sbLocale)
      if (suggestions != undefined && suggestions.hasResults && suggestions.products.length > 0) {
        setFoundProducts(suggestions.products.filter((s) => !s.id.toUpperCase().startsWith("E")))
      } else {
        setFoundProducts([])
      }
    })
  }

  async function handleAddProductToWishlist(product: SuggestionItem) {
    setCurrentAddItem(product.id)
    transitionAddItem(async () => {
      await addProductToWishlistAction(props.wishlistId, product.id, 1, true)
      await props.onChange()
    })
  }

  async function resetSearch() {
    if (searchFieldRef.current) {
      searchFieldRef.current.value = ""
      setFoundProducts(null)
    }
  }

  return (
    <div className={"mt-6 flex flex-col gap-2 rounded-sm border-gray-300 bg-gray-200 p-4"}>
      <div className={"relative w-full"}>
        {!loadingSearch && (foundProducts || searchFieldRef?.current?.value) ? (
          <button onClick={resetSearch} className={"absolute right-2 top-1/2 size-5 -translate-y-1/2 text-gray-400"}>
            <PlusIcon strokeWidth={2} className={"size-5 rotate-45"} />
          </button>
        ) : (
          <div className={"pointer-events-none absolute right-2 top-1/2 size-5 -translate-y-1/2 text-gray-400"}>
            {loadingSearch ? (
              <LoaderCircle strokeWidth={2} className="size-5 motion-safe:animate-spin" />
            ) : (
              <SearchIcon strokeWidth={2} className="size-5" />
            )}
          </div>
        )}
        <Input
          ref={searchFieldRef}
          type={"text"}
          onChange={(e) => debouncedHandleSearch(e.target.value)}
          className="w-full rounded-lg border border-gray-300 py-2 pl-4 pr-10 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={props.blok.label_search_placeholder}
        />
      </div>
      {foundProducts !== null &&
        (foundProducts.length === 0 ? (
          <p className={"text-gray-700"}>{props.blok.label_search_no_result}</p>
        ) : (
          <div
            className={
              "grid grid-cols-[min-content_auto_max-content] gap-2 lg:grid-cols-[max-content_max-content_auto_max-content]"
            }
          >
            {foundProducts.map((product: SuggestionItem) => (
              <div
                key={product.id}
                className="col-span-full grid grid-cols-subgrid items-center gap-2  rounded-lg border border-gray-400 bg-white p-2 text-sm"
              >
                <Link
                  prefetch={false}
                  className={
                    "flex w-fit cursor-pointer flex-col justify-between underline hover:text-primary lg:col-span-2 lg:grid lg:grid-cols-subgrid"
                  }
                  href={generateProductLink(product?.name, product?.id, undefined, props.sbLocale)}
                >
                  <div className="flex justify-center">
                    <ImageWithFallback
                      className={"aspect-square h-10 object-contain lg:h-20"}
                      src={product?.image ?? ""}
                      width={80}
                      height={80}
                      alt={product?.name ?? ""}
                    />
                  </div>
                  <p className="flex items-center text-xs md:text-sm">
                    {determinePrefixedProductId(product?.id ?? "")}
                  </p>
                </Link>
                <div>
                  <p className={"hyphens-auto text-xs max-lg:line-clamp-5 md:text-sm lg:max-w-[48ch] "}>
                    {product?.name ?? ""}
                  </p>
                </div>
                <div>
                  <Button
                    size={"smNoGrow"}
                    variant={"outline"}
                    className="bg-primary text-white"
                    onClick={() => handleAddProductToWishlist(product)}
                    disabled={loadingAddItem}
                  >
                    {loadingAddItem && currentAddItem == product.id ? (
                      <>
                        <LoaderCircle className={"size-5 motion-safe:animate-spin"} />{" "}
                        {props.blok.label_search_add_item_working}
                      </>
                    ) : (
                      <>
                        <PlusIcon className={"size-5"} /> {props.blok.label_search_add_item}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ))}
    </div>
  )
}
